import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type {
  BookingState,
  BookingContextValue,
  ServiceType,
  GuaranteeType,
  SelectedExperience,
  SelectedArea,
  SelectionOrigin,
} from './types';
import type { SelectedPairing } from '../types/pairings';
import { DEFAULT_BOOKING_STATE, CARD_FIELDS_RESET } from './types';
import type { ProcessedSlot, TermAndCondition } from '../types/timeSlots';
import { safeSessionStorage } from '@shared/utils/safeStorage';
import { useTenant } from '@core/tenant/TenantProvider';
import { consumeFreshStart, ownFrameId } from '@core/embed';

/**
 * Extract yyyy-MM-dd from any date string (ISO "2026-04-14T03:00:00.000Z" or date-only "2026-04-14").
 * Returns the value as-is if shorter than 10 characters.
 */
function normalizeDateKey(value: string): string {
  if (value.length <= 10) return value;
  return value.slice(0, 10);
}

/**
 * Storage key for booking state persistence
 */
const BOOKING_STORAGE_KEY = 'dineflow_booking_state';

/**
 * Session storage is shared by every same-origin frame on a page, so a site
 * embedding an inline widget and a floating one would otherwise have each
 * frame overwrite, and a fresh floating start wipe, the other's booking.
 */
export function bookingStorageKey(): string {
  const frameId = ownFrameId();
  return frameId ? `${BOOKING_STORAGE_KEY}:${frameId}` : BOOKING_STORAGE_KEY;
}

/**
 * Max age for stored booking state (2 hours)
 */
const BOOKING_STATE_MAX_AGE = 2 * 60 * 60 * 1000;

/**
 * Booking context
 */
const BookingContext = createContext<BookingContextValue | null>(null);

interface BookingProviderProps {
  children: React.ReactNode;
}

/**
 * Load booking state from sessionStorage
 */
export function loadStoredBookingState(): BookingState | null {
  const stored = safeSessionStorage.getItem(bookingStorageKey());
  if (!stored) return null;

  try {
    const stored_state = JSON.parse(stored) as Partial<BookingState>;

    if (Date.now() - (stored_state.startedAt ?? 0) > BOOKING_STATE_MAX_AGE) {
      safeSessionStorage.removeItem(bookingStorageKey());
      return null;
    }

    const merged: BookingState = {
      ...DEFAULT_BOOKING_STATE,
      ...stored_state,
      selectedDate: stored_state.selectedDate
        ? normalizeDateKey(stored_state.selectedDate)
        : DEFAULT_BOOKING_STATE.selectedDate,
    };

    // T11: a pre-T11 stored session won't include `skipConfirmStep`. Erase the
    // default-merged value so BookingProvider can distinguish "stored sessions
    // pre-T11" (stamp from current config) from "stored sessions post-T11"
    // (preserve the frozen value, even if it's the default `false`).
    if (!('skipConfirmStep' in stored_state)) {
      delete (merged as Partial<BookingState>).skipConfirmStep;
    }

    return merged;
  } catch {
    return null;
  }
}

/**
 * Save booking state to sessionStorage
 */
function saveBookingState(state: BookingState): void {
  safeSessionStorage.setItem(bookingStorageKey(), JSON.stringify(state));
}

/**
 * Clear booking state from sessionStorage
 */
function clearBookingState(): void {
  safeSessionStorage.removeItem(bookingStorageKey());
}

/**
 * Card, guarantee and tip fields cleared by any setter whose change can alter
 * the guarantee (type or amount) the card was issued for, or the active menu
 * a saved tip pick was chosen against. `setSelectedExperience`'s same-id
 * branch is the one place that deliberately does NOT spread this — re-picking
 * the same experience preserves the guest's tip choice.
 */
const PAYMENT_STATE_RESET = {
  ...CARD_FIELDS_RESET,
  paymentProcessor: null,
  guaranteeType: 'traditional' as const,
  feeDeposit: 0,
  feeGratuity: 0,
  feeGratuityPercent: 0,
  tipChargePercent: null,
} satisfies Partial<BookingState>;

/**
 * BookingProvider component that manages booking flow state
 * with persistence across page refreshes
 */
export function BookingProvider({ children }: BookingProviderProps) {
  const [state, setState] = useState<BookingState>(DEFAULT_BOOKING_STATE);
  const [isLoading, setIsLoading] = useState(true);
  // Use `useTenant` (which doesn't throw when restaurant is missing) instead of
  // `useRestaurant`. Some contexts mount BookingProvider before the tenant
  // payload arrives (`/verify/*` harness fixtures without `seed.restaurant`,
  // and TenantProvider's own loading window). The frozen-flag effect treats
  // any non-`true` value as `false`, so a null restaurant just preserves
  // legacy Confirm-step behavior — the correct default.
  const { restaurant } = useTenant();

  // Effect 1: hydrate from sessionStorage on mount. Always runs once and
  // sets `isLoading` false so consumers can render — does NOT depend on
  // `restaurant`. The auth subdomain and `/verify/*` harness mount this
  // provider without a tenant payload; making isLoading wait on restaurant
  // would deadlock those trees.
  useEffect(() => {
    // The v2 floating widget reopens from scratch.
    if (consumeFreshStart()) {
      clearBookingState();
      setIsLoading(false);
      return;
    }
    const storedState = loadStoredBookingState();
    if (storedState) {
      setState(storedState);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect 2: sync `skipConfirmStep` with the live restaurant config.
  //
  // History: the original T11 design tried to freeze this value at session
  // start so admin flag-flips wouldn't change the step set mid-booking.
  // That design + a subsequent fix attempt both had subtle correctness
  // bugs (race with TenantProvider's async load; stored-value-wins
  // persisting poisoned values across deploys). After 2026-05-21 we
  // abandoned the freeze: we stamp `skipConfirmStep` from the current
  // restaurant config every time restaurant changes.
  //
  // Trade-off: an admin flag-flip mid-booking DOES propagate to the
  // active user's session. The cost is at most a one-step churn — either
  // CONFIRMATION appears or disappears between the user's current page
  // and their next click. We accept this because the alternative (frozen)
  // shipped two failed implementations and a production-blocking
  // poisoned-state bug. Flag-flips happen at most a few times per tenant
  // per quarter, and both code paths (legacy /confirmation and skip) are
  // correct on their own; the worst case is the user sees /confirmation
  // once when they didn't expect to.
  //
  // If we ever need true frozen-on-init back, the right design is a
  // session-payload version field: bump the version when the contract
  // changes, invalidate stored payloads whose version is below the
  // current build's version. Filed as followup, not in scope tonight.
  useEffect(() => {
    if (!restaurant) return;
    const skipFromConfig = restaurant.bookingSkipConfirmationV2 === true;
    setState((prev) =>
      prev.skipConfirmStep === skipFromConfig
        ? prev
        : { ...prev, skipConfirmStep: skipFromConfig },
    );
  }, [restaurant]);

  // Persist state to storage whenever it changes
  useEffect(() => {
    if (!isLoading) {
      saveBookingState(state);
    }
  }, [state, isLoading]);

  // Actions
  const setDiners = useCallback((diners: number) => {
    setState((prev) => prev.diners === diners
      ? prev
      : { ...prev, diners, timeSlots: null, selectedTime: null, columnType: null, ...PAYMENT_STATE_RESET });
  }, []);

  const setSelectedDate = useCallback((selectedDate: string | null) => {
    const normalized = selectedDate ? normalizeDateKey(selectedDate) : null;
    setState((prev) => prev.selectedDate === normalized
      ? prev
      : { ...prev, selectedDate: normalized, timeSlots: null, selectedTime: null, columnType: null, ...PAYMENT_STATE_RESET });
  }, []);

  const setService = useCallback((service: ServiceType | null) => {
    setState((prev) => prev.service === service
      ? prev
      : { ...prev, service, timeSlots: null, selectedTime: null, columnType: null, ...PAYMENT_STATE_RESET });
  }, []);

  const setSelectedTime = useCallback((selectedTime: string | null) => {
    setState((prev) => prev.selectedTime === selectedTime
      ? prev
      : { ...prev, selectedTime, selectedPairings: [], ...PAYMENT_STATE_RESET });
  }, []);

  const setSelectedExperience = useCallback((selectedExperience: SelectedExperience | null, origin?: SelectionOrigin) => {
    const menuSelectedByGuest = !!origin?.byGuest;
    setState((prev) => {
      const prevId = prev.selectedExperience?.id ?? null;
      const nextId = selectedExperience?.id ?? null;
      if (prevId === nextId) {
        return { ...prev, selectedExperience, menuSelectedByGuest };
      }
      return { ...prev, selectedExperience, menuSelectedByGuest, selectedPairings: [], ...PAYMENT_STATE_RESET };
    });
  }, []);

  const setSelectedArea = useCallback((selectedArea: SelectedArea | null, origin?: SelectionOrigin) => {
    const areaSelectedByGuest = !!origin?.byGuest;
    setState((prev) => {
      const prevId = prev.selectedArea?.id ?? null;
      const nextId = selectedArea?.id ?? null;
      if (String(prevId) === String(nextId)) {
        return { ...prev, selectedArea, areaSelectedByGuest };
      }
      return { ...prev, selectedArea, areaSelectedByGuest, selectedPairings: [], ...PAYMENT_STATE_RESET };
    });
  }, []);

  const setShowExperience = useCallback((showExperience: boolean) => {
    setState((prev) => ({ ...prev, showExperience }));
  }, []);

  const setSpecialRequests = useCallback((specialRequests: string) => {
    setState((prev) => ({ ...prev, specialRequests }));
  }, []);

  const setSlotTerms = useCallback((slotTerms: TermAndCondition[]) => {
    setState((prev) => ({ ...prev, slotTerms }));
  }, []);

  const setTimeSlots = useCallback((timeSlots: ProcessedSlot[] | null) => {
    setState((prev) => ({ ...prev, timeSlots }));
  }, []);

  // Personalization setters. Allergies/veggie/kids/pets can flip the resolved
  // guarantee type via slot.specialRequestGuaranties, so payment state resets
  // when the value changes.
  const setAllergies = useCallback((allergies: string) => {
    setState((prev) => prev.allergies === allergies
      ? prev
      : { ...prev, allergies, ...PAYMENT_STATE_RESET });
  }, []);

  const setVeggie = useCallback((veggie: boolean) => {
    setState((prev) => prev.veggie === veggie
      ? prev
      : { ...prev, veggie, ...PAYMENT_STATE_RESET });
  }, []);

  const setHowManyVeggie = useCallback((howManyVeggie: number) => {
    setState((prev) => ({ ...prev, howManyVeggie }));
  }, []);

  const setKids = useCallback((kids: boolean) => {
    setState((prev) => prev.kids === kids
      ? prev
      : { ...prev, kids, ...PAYMENT_STATE_RESET });
  }, []);

  const setHowManyKids = useCallback((howManyKids: number) => {
    setState((prev) => ({ ...prev, howManyKids }));
  }, []);

  const setPets = useCallback((pets: boolean) => {
    setState((prev) => prev.pets === pets
      ? prev
      : { ...prev, pets, ...PAYMENT_STATE_RESET });
  }, []);

  const setAccessibility = useCallback((accessibility: boolean) => {
    setState((prev) => ({ ...prev, accessibility }));
  }, []);

  // Guarantee/Payment setters
  const setGuaranteeType = useCallback((guaranteeType: GuaranteeType) => {
    setState((prev) => ({ ...prev, guaranteeType }));
  }, []);

  const setCardToken = useCallback((cardToken: string | null) => {
    setState((prev) => ({ ...prev, cardToken }));
  }, []);

  const setFeeDeposit = useCallback((feeDeposit: number) => {
    setState((prev) => ({ ...prev, feeDeposit }));
  }, []);

  const setPaymentMethod = useCallback((paymentMethod: string | null) => {
    setState((prev) => ({ ...prev, paymentMethod }));
  }, []);

  const setSelectedMenuFilter = useCallback((selectedMenuFilter: number[] | null, origin?: SelectionOrigin) => {
    const menuFilterByGuest = !!origin?.byGuest;
    setState((prev) => {
      const before = prev.selectedMenuFilter ?? [];
      const after = selectedMenuFilter ?? [];
      const same = before.length === after.length && before.every((id, i) => id === after[i]);
      return same
        ? { ...prev, selectedMenuFilter, menuFilterByGuest }
        : { ...prev, selectedMenuFilter, menuFilterByGuest, selectedPairings: [], ...PAYMENT_STATE_RESET };
    });
  }, []);

  const setAreaFilterIds = useCallback((areaFilterIds: number[] | null, origin?: SelectionOrigin) => {
    setState((prev) => ({
      ...prev,
      areaFilterIds,
      areaFilterByGuest: !!origin?.byGuest,
      timeSlots: null,
      selectedTime: null,
      columnType: null,
      selectedArea: null,
      areaSelectedByGuest: false,
      selectedExperience: null,
      menuSelectedByGuest: false,
      slotTerms: [],
      tipChargePercent: null,
    }));
  }, []);

  const setSelectedPairings = useCallback((selectedPairings: SelectedPairing[]) => {
    setState((prev) => {
      const sameLength = prev.selectedPairings.length === selectedPairings.length;
      const sameItems = sameLength && prev.selectedPairings.every((p, i) =>
        p.id === selectedPairings[i].id && p.quantity === selectedPairings[i].quantity);
      if (sameItems) {
        return { ...prev, selectedPairings };
      }
      return { ...prev, selectedPairings, ...PAYMENT_STATE_RESET };
    });
  }, []);

  const setColumnType = useCallback((columnType: string | null) => {
    setState((prev) => ({ ...prev, columnType }));
  }, []);

  const setIsForeign = useCallback((isForeign: boolean) => {
    setState((prev) => prev.isForeign === isForeign
      ? prev
      : { ...prev, isForeign, ...PAYMENT_STATE_RESET });
  }, []);

  const setTipChargePercent = useCallback((tipChargePercent: number | null) => {
    setState((prev) => ({ ...prev, tipChargePercent }));
  }, []);

  // Reservation result setter
  const setReservationResult = useCallback((reservationId: string, uniqueId?: string) => {
    setState((prev) => ({ ...prev, reservationId, uniqueId: uniqueId ?? null }));
  }, []);

  // Guest info setter
  const setGuestInfo = useCallback((guestName: string, guestEmail: string, guestPhone: string) => {
    setState((prev) => ({ ...prev, guestName, guestEmail, guestPhone }));
  }, []);

  // User note setter
  const setUserNote = useCallback((userNote: string) => {
    setState((prev) => ({ ...prev, userNote }));
  }, []);

  // Waiting list setters
  const setWlDates = useCallback((wlDates: string[]) => {
    setState((prev) => ({ ...prev, wlDates }));
  }, []);

  const setWlPreferredTime = useCallback((wlPreferredTime: string | null) => {
    setState((prev) => ({ ...prev, wlPreferredTime }));
  }, []);

  const setWlMenus = useCallback((wlMenus: number[]) => {
    setState((prev) => {
      const same = prev.wlMenus.length === wlMenus.length && prev.wlMenus.every((id, i) => id === wlMenus[i]);
      return same ? { ...prev, wlMenus } : { ...prev, wlMenus, selectedPairings: [], ...PAYMENT_STATE_RESET };
    });
  }, []);

  const setWlServiceType = useCallback((wlServiceType: ServiceType | null) => {
    setState((prev) => ({ ...prev, wlServiceType }));
  }, []);

  const resetBooking = useCallback(() => {
    clearBookingState();
    // CRITICAL: preserve `skipConfirmStep` across reset. The live-sync
    // effect only re-fires on `restaurant` change. After a reset (e.g.
    // post-booking from BookingFinalPage, date change from profile,
    // landing-page nav), `restaurant` does NOT change because the tenant
    // didn't change — so a plain DEFAULT_BOOKING_STATE wipe would leave
    // `skipConfirmStep` stuck at the default `false` until the user reloads
    // the page or switches subdomains. Result: a user who successfully
    // skipped /confirmation on their first booking sees /confirmation on
    // their second booking in the same tab. Stamping from the live
    // restaurant value here keeps reset idempotent w.r.t. the flag.
    setState((prev) => ({
      ...DEFAULT_BOOKING_STATE,
      startedAt: Date.now(),
      skipConfirmStep: restaurant
        ? restaurant.bookingSkipConfirmationV2 === true
        : prev.skipConfirmStep,
    }));
  }, [restaurant]);

  const updateBooking = useCallback((updates: Partial<BookingState>) => {
    if (updates.selectedDate) {
      updates = { ...updates, selectedDate: normalizeDateKey(updates.selectedDate) };
    }
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Computed values
  const isValid = useMemo(() => {
    return state.diners > 0 && state.selectedDate !== null;
  }, [state.diners, state.selectedDate]);

  const contextValue: BookingContextValue = {
    // State
    ...state,
    isLoading,
    isValid,
    // Actions
    setDiners,
    setSelectedDate,
    setService,
    setSelectedTime,
    setSelectedExperience,
    setSelectedArea,
    setShowExperience,
    setSpecialRequests,
    setSlotTerms,
    setTimeSlots,
    setAllergies,
    setVeggie,
    setHowManyVeggie,
    setKids,
    setHowManyKids,
    setPets,
    setAccessibility,
    setGuaranteeType,
    setCardToken,
    setFeeDeposit,
    setPaymentMethod,
    setSelectedMenuFilter,
    setAreaFilterIds,
    setSelectedPairings,
    setColumnType,
    setIsForeign,
    setTipChargePercent,
    setReservationResult,
    setGuestInfo,
    setUserNote,
    setWlDates,
    setWlPreferredTime,
    setWlMenus,
    setWlServiceType,
    resetBooking,
    updateBooking,
  };

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
}

/**
 * Hook to access full booking context
 */
export function useBooking(): BookingContextValue {
  const context = useContext(BookingContext);

  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }

  return context;
}

/**
 * Hook to get just the booking state (no actions).
 * Automatically picks up new BookingState fields without manual listing.
 */
export function useBookingState(): BookingState & { isLoading: boolean; isValid: boolean } {
  const {
    // Destructure and discard all actions so the returned object contains only state
    setDiners: _, setSelectedDate: _1, setService: _2, setSelectedTime: _3,
    setSelectedExperience: _4, setSelectedArea: _5, setShowExperience: _6,
    setSpecialRequests: _7, setSlotTerms: _7b, setTimeSlots: _8, setAllergies: _9, setVeggie: _10,
    setHowManyVeggie: _11, setKids: _12, setHowManyKids: _13, setPets: _14,
    setAccessibility: _15, setGuaranteeType: _17,
    setCardToken: _18, setFeeDeposit: _19, setPaymentMethod: _20,
    setSelectedMenuFilter: _26, setAreaFilterIds: _26b, setSelectedPairings: _27, setColumnType: _28,
    setIsForeign: _29, setTipChargePercent: _30,
    setReservationResult: _21, setGuestInfo: _22, setUserNote: _23,
    setWlDates: _31, setWlPreferredTime: _32, setWlMenus: _33, setWlServiceType: _34,
    resetBooking: _24, updateBooking: _25,
    ...state
  } = useBooking();

  return state;
}

/**
 * Hook to get just the booking actions
 */
export function useBookingActions() {
  const booking = useBooking();

  return {
    setDiners: booking.setDiners,
    setSelectedDate: booking.setSelectedDate,
    setService: booking.setService,
    setSelectedTime: booking.setSelectedTime,
    setTimeSlots: booking.setTimeSlots,
    setSelectedExperience: booking.setSelectedExperience,
    setSelectedArea: booking.setSelectedArea,
    setShowExperience: booking.setShowExperience,
    setSpecialRequests: booking.setSpecialRequests,
    setSlotTerms: booking.setSlotTerms,
    setAllergies: booking.setAllergies,
    setVeggie: booking.setVeggie,
    setHowManyVeggie: booking.setHowManyVeggie,
    setKids: booking.setKids,
    setHowManyKids: booking.setHowManyKids,
    setPets: booking.setPets,
    setAccessibility: booking.setAccessibility,
    setGuaranteeType: booking.setGuaranteeType,
    setCardToken: booking.setCardToken,
    setFeeDeposit: booking.setFeeDeposit,
    setPaymentMethod: booking.setPaymentMethod,
    setSelectedMenuFilter: booking.setSelectedMenuFilter,
    setAreaFilterIds: booking.setAreaFilterIds,
    setSelectedPairings: booking.setSelectedPairings,
    setColumnType: booking.setColumnType,
    setIsForeign: booking.setIsForeign,
    setTipChargePercent: booking.setTipChargePercent,
    setReservationResult: booking.setReservationResult,
    setGuestInfo: booking.setGuestInfo,
    setUserNote: booking.setUserNote,
    setWlDates: booking.setWlDates,
    setWlPreferredTime: booking.setWlPreferredTime,
    setWlMenus: booking.setWlMenus,
    setWlServiceType: booking.setWlServiceType,
    resetBooking: booking.resetBooking,
    updateBooking: booking.updateBooking,
  };
}

export default BookingProvider;
